/**
 * Role-based permissions hook for the restaurant management system
 * Implements the 7-level hierarchy:
 * 1. ADMIN - Full access to everything including financial data and settings
 * 2. DIRECTEUR - Operational access, no financial margins/costs visible
 * 3. DIRECTEUR_ADJOINT - Limited operational, no financial data
 * 4. CHEF_CUISINE - Kitchen management, recipes, ingredient stock
 * 5. CUISINIER - View recipes, view ingredient stock
 * 6. SERVEUR/SERVEUSE - POS sales only, own orders only
 * 7. MAITRE_HOTEL - Same as SERVEUR plus reservations
 */

const ROLE_HIERARCHY = {
    ADMIN: 7,
    DIRECTEUR: 6,
    DIRECTEUR_ADJOINT: 5,
    CHEF_CUISINE: 4,
    CUISINIER: 3,
    MAITRE_HOTEL: 2,
    SERVEUR: 1,
    SERVEUSE: 1
};

/**
 * Permission definitions by feature
 * Each permission maps to a minimum role level or specific roles
 */
const PERMISSIONS = {
    // Dashboard Admin
    VIEW_ADMIN_DASHBOARD: ['ADMIN'],
    VIEW_PROFITABILITY: ['ADMIN'],
    VIEW_MARGINS: ['ADMIN'],
    VIEW_COSTS: ['ADMIN'],
    EDIT_FIXED_CHARGES: ['ADMIN'],
    
    // Stock & Products
    VIEW_STOCK: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE', 'CUISINIER'],
    EDIT_STOCK: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE'],
    VIEW_PURCHASE_PRICE: ['ADMIN'],
    EDIT_PURCHASE_PRICE: ['ADMIN'],
    VIEW_SELLING_PRICE: ['ADMIN', 'DIRECTEUR'],
    EDIT_SELLING_PRICE: ['ADMIN'],
    
    // Recipes
    VIEW_RECIPES: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE', 'CUISINIER'],
    EDIT_RECIPES: ['ADMIN', 'DIRECTEUR', 'CHEF_CUISINE'],
    VIEW_RECIPE_COSTS: ['ADMIN'],
    
    // POS
    ACCESS_POS: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'SERVEUR', 'SERVEUSE', 'MAITRE_HOTEL'],
    PROCESS_SALES: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'SERVEUR', 'SERVEUSE', 'MAITRE_HOTEL'],
    APPLY_DISCOUNTS: ['ADMIN'],
    VIEW_ALL_ORDERS: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
    VIEW_POS_REPORTS: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
    MANAGE_POS_PRODUCTS: ['ADMIN', 'DIRECTEUR'],
    SUPERVISE_POS: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
    
    // Caisse unifiée - Nouvelles permissions
    VIEW_ALL_TABLES: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
    VIEW_OWN_TABLES: ['SERVEUR', 'SERVEUSE', 'MAITRE_HOTEL'],
    ENCAISSER_ALL_TABLES: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
    ENCAISSER_OWN_TABLES: ['SERVEUR', 'SERVEUSE', 'MAITRE_HOTEL'],
    CANCEL_ORDER: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
    ACCESS_ADMIN_SETTINGS: ['ADMIN'],
    VIEW_CA_JOUR: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
    VIEW_SUPERVISION_BAR: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
    
    // Sessions
    OPEN_SESSION_FOR_OTHERS: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
    CLOSE_OWN_SESSION: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'SERVEUR', 'SERVEUSE', 'MAITRE_HOTEL'],
    VALIDATE_SESSION_CLOSURE: ['ADMIN', 'DIRECTEUR'],
    
    // Users & Administration
    VIEW_USERS: ['ADMIN', 'DIRECTEUR'],
    EDIT_USERS: ['ADMIN'],
    ASSIGN_ROLES: ['ADMIN'],
    
    // Reports
    VIEW_Z_REPORT: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
    EXPORT_REPORTS: ['ADMIN', 'DIRECTEUR'],
    
    // Stock Corrections
    REQUEST_STOCK_CORRECTION: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE'],
    VALIDATE_STOCK_CORRECTION: ['ADMIN'],
    
    // Audit Log
    VIEW_AUDIT_LOG: ['ADMIN'],
    
    // Reservations
    VIEW_RESERVATIONS: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'MAITRE_HOTEL', 'SERVEUR', 'SERVEUSE'],
    MANAGE_RESERVATIONS: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'MAITRE_HOTEL'],
    
    // Suppliers
    VIEW_SUPPLIERS: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE'],
    MANAGE_SUPPLIERS: ['ADMIN', 'DIRECTEUR'],
    
    // Stock Alerts
    VIEW_STOCK_ALERTS: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE'],
    ACKNOWLEDGE_ALERTS: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE'],
    
    // Menu
    VIEW_MENU: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE', 'CUISINIER', 'SERVEUR', 'SERVEUSE'],
    EDIT_MENU: ['ADMIN', 'DIRECTEUR', 'CHEF_CUISINE'],
    
    // Planning
    VIEW_PLANNING: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE', 'CUISINIER', 'SERVEUR', 'SERVEUSE', 'MAITRE_HOTEL'],
    EDIT_PLANNING: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT']
};

/**
 * Check if a role has a specific permission
 * @param {string} role - User's role
 * @param {string} permission - Permission key from PERMISSIONS
 * @returns {boolean}
 */
export const hasPermission = (role, permission) => {
    if (!role || !permission) return false;
    const allowedRoles = PERMISSIONS[permission];
    if (!allowedRoles) return false;
    return allowedRoles.includes(role);
};

/**
 * Check if role1 has higher or equal authority than role2
 * @param {string} role1 - First role
 * @param {string} role2 - Second role
 * @returns {boolean}
 */
export const hasHigherOrEqualAuthority = (role1, role2) => {
    const level1 = ROLE_HIERARCHY[role1] || 0;
    const level2 = ROLE_HIERARCHY[role2] || 0;
    return level1 >= level2;
};

/**
 * Get role display name in French
 * @param {string} role - Role key
 * @returns {string}
 */
export const getRoleDisplayName = (role) => {
    const names = {
        ADMIN: 'Administrateur',
        DIRECTEUR: 'Directeur',
        DIRECTEUR_ADJOINT: 'Directeur Adjoint',
        CHEF_CUISINE: 'Chef de Cuisine',
        CUISINIER: 'Cuisinier',
        SERVEUR: 'Serveur',
        SERVEUSE: 'Serveuse',
        MAITRE_HOTEL: "Maître d'Hôtel"
    };
    return names[role] || role;
};

/**
 * Get role color for UI elements
 * @param {string} role - Role key
 * @returns {string} - Tailwind color class
 */
export const getRoleColor = (role) => {
    const colors = {
        ADMIN: 'bg-purple-100 text-purple-800 border-purple-300',
        DIRECTEUR: 'bg-blue-100 text-blue-800 border-blue-300',
        DIRECTEUR_ADJOINT: 'bg-cyan-100 text-cyan-800 border-cyan-300',
        CHEF_CUISINE: 'bg-orange-100 text-orange-800 border-orange-300',
        CUISINIER: 'bg-amber-100 text-amber-800 border-amber-300',
        SERVEUR: 'bg-green-100 text-green-800 border-green-300',
        SERVEUSE: 'bg-green-100 text-green-800 border-green-300',
        MAITRE_HOTEL: 'bg-teal-100 text-teal-800 border-teal-300'
    };
    return colors[role] || 'bg-gray-100 text-gray-800 border-gray-300';
};

/**
 * Hook to use permissions in React components
 * @param {object} user - User object with role property
 * @returns {object} - Permission checking functions
 */
export const usePermissions = (user) => {
    const role = user?.role;
    
    return {
        // General permission check
        can: (permission) => hasPermission(role, permission),
        
        // Role checks
        isAdmin: role === 'ADMIN',
        isDirector: role === 'DIRECTEUR' || role === 'DIRECTEUR_ADJOINT',
        isManagement: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(role),
        isKitchen: ['ADMIN', 'CHEF_CUISINE', 'CUISINIER'].includes(role),
        isSales: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'SERVEUR', 'SERVEUSE', 'MAITRE_HOTEL'].includes(role),
        isServer: ['SERVEUR', 'SERVEUSE', 'MAITRE_HOTEL'].includes(role),
        
        // Specific permission shortcuts
        canViewMargins: hasPermission(role, 'VIEW_MARGINS'),
        canViewCosts: hasPermission(role, 'VIEW_COSTS'),
        canApplyDiscounts: hasPermission(role, 'APPLY_DISCOUNTS'),
        canManagePOSProducts: hasPermission(role, 'MANAGE_POS_PRODUCTS'),
        canSupervisePOS: hasPermission(role, 'SUPERVISE_POS'),
        canEditStock: hasPermission(role, 'EDIT_STOCK'),
        canEditPrices: hasPermission(role, 'EDIT_SELLING_PRICE'),
        canViewAuditLog: hasPermission(role, 'VIEW_AUDIT_LOG'),
        canValidateCorrections: hasPermission(role, 'VALIDATE_STOCK_CORRECTION'),
        
        // Caisse unifiée - Permissions shortcuts
        canViewAllTables: hasPermission(role, 'VIEW_ALL_TABLES'),
        canViewOwnTablesOnly: hasPermission(role, 'VIEW_OWN_TABLES') && !hasPermission(role, 'VIEW_ALL_TABLES'),
        canEncaisserAllTables: hasPermission(role, 'ENCAISSER_ALL_TABLES'),
        canEncaisserOwnTablesOnly: hasPermission(role, 'ENCAISSER_OWN_TABLES') && !hasPermission(role, 'ENCAISSER_ALL_TABLES'),
        canCancelOrder: hasPermission(role, 'CANCEL_ORDER'),
        canAccessAdminSettings: hasPermission(role, 'ACCESS_ADMIN_SETTINGS'),
        canViewCAJour: hasPermission(role, 'VIEW_CA_JOUR'),
        canViewSupervisionBar: hasPermission(role, 'VIEW_SUPERVISION_BAR'),
        
        // Authority check
        hasAuthorityOver: (otherRole) => hasHigherOrEqualAuthority(role, otherRole),
        
        // Display helpers
        roleDisplayName: getRoleDisplayName(role),
        roleColor: getRoleColor(role)
    };
};

export default usePermissions;
